import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  TrendingDown, TrendingUp, RefreshCw, Wallet, Calendar,
  Loader2, ArrowRight, Building2,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';

export default function GlobalCreditDebitPage({ noLayout = false }) {
  const [partner, setPartner] = useState('Anchor');
  const [partners, setPartners] = useState(['Anchor', 'BillPortal']);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { partner };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: d } = await api.get('/bank-statements/global-cd-report', { params });
      setData(d);
    } catch (e) {
      toast.error('Failed to load global report');
    } finally {
      setLoading(false);
    }
  }, [partner, dateFrom, dateTo]);

  useEffect(() => {
    api.get('/bank-statements/partners').then(({ data }) => {
      setPartners(data.partners || ['Anchor']);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals || {};
  const allRows = data?.rows || [];
  const rows = search
    ? allRows.filter(r => (r.org_name || '').toLowerCase().includes(search.toLowerCase()))
    : allRows;

  // Conditionally wrap in MainLayout — when embedded inside SuperAdmin, skip the wrapper.
  const Wrapper = noLayout ? ({ children }) => <>{children}</> : MainLayout;

  return (
    <Wrapper>
      <div className="space-y-5" data-testid="global-credit-debit-page">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Global Credit / Debit Report</h2>
          <p className="text-sm text-muted-foreground">
            Super-admin view: by Partner → Org. Shows uploaded bank credits vs combined bill debits
            (payments + service charges + completed refunds). Uploads count: {totals.uploads_count || 0} ·
            Period: {fmtD(totals.earliest_period)} to {fmtD(totals.latest_period)}.
          </p>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="global-cd-top-stats">
          <div className="border rounded-lg p-4 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600">Current Balance</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${totals.current_balance < 0 ? 'text-red-700' : ''}`} data-testid="global-stat-current">{fmtC(totals.current_balance)}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Credits − Debits (settled)</p>
          </div>
          <div className="border rounded-lg p-4 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-600">Available Balance</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${totals.available_balance < 0 ? 'text-red-700' : ''}`} data-testid="global-stat-available">{fmtC(totals.available_balance)}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Current − {fmtC(totals.pending_refund || 0)} pending refunds</p>
          </div>
          <div className="border rounded-lg p-4 bg-emerald-50/40">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Total Credits (YTD)</span>
            </div>
            <p className="text-xl font-bold text-emerald-700 tabular-nums" data-testid="global-stat-ytd-credits">{fmtC(totals.ytd_credits)}</p>
          </div>
          <div className="border rounded-lg p-4 bg-red-50/40">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="h-3.5 w-3.5 text-red-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Total Debits (YTD)</span>
            </div>
            <p className="text-xl font-bold text-red-700 tabular-nums" data-testid="global-stat-ytd-debits">{fmtC(totals.ytd_debits)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="border rounded-lg p-3 bg-card flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Partner</span>
            <Select value={partner} onValueChange={setPartner}>
              <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="global-filter-partner"><SelectValue /></SelectTrigger>
              <SelectContent>
                {partners.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-[140px]" />
            <span className="text-muted-foreground text-xs">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-[140px]" />
          </div>
          <Input
            placeholder="Search org..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs w-[200px]"
            data-testid="global-search"
          />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8 text-xs" data-testid="global-refresh-btn">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
          <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
            {loading ? '...' : `${rows.length} of ${allRows.length} orgs`}
          </span>
        </div>

        {/* Org table */}
        <div className="border rounded-lg bg-card overflow-x-auto">
          <table className="w-full text-xs" data-testid="global-cd-table">
            <thead className="bg-muted/30 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Organisation</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-emerald-600">Credits</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-red-600">Debits</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Bill Pmts</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Svc Charges</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Refunds</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Net</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Bills</th>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Last Credit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}><td colSpan={9} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-muted-foreground">
                    <Building2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No organisations match the filters</p>
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr
                    key={r.account_id}
                    className="border-b border-border/40 hover:bg-muted/30 cursor-pointer"
                    data-testid={`global-cd-row-${i}`}
                  >
                    <td className="px-3 py-2 font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{r.org_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{fmtC(r.credits)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700 font-semibold">{fmtC(r.debits)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtC(r.bill_payments)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.service_charges > 0 ? fmtC(r.service_charges) : '\u2014'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.refunds > 0 ? fmtC(r.refunds) : '\u2014'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.net > 0 ? 'text-emerald-600' : r.net < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {fmtC(r.net)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.bill_count.toLocaleString()}</td>
                    <td className="px-3 py-2 text-muted-foreground text-[11px]">{fmtD(r.last_credit_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {/* Totals row */}
            {!loading && rows.length > 0 && (
              <tfoot className="bg-muted/40 sticky bottom-0">
                <tr className="border-t-2 border-border font-semibold" data-testid="global-cd-totals-row">
                  <td className="px-3 py-2 text-[11px] uppercase tracking-wider">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtC(totals.credits)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">{fmtC(totals.debits)}</td>
                  <td colSpan={3}></td>
                  <td className={`px-3 py-2 text-right tabular-nums ${totals.net > 0 ? 'text-emerald-600' : totals.net < 0 ? 'text-red-600' : ''}`}>
                    {fmtC(totals.net)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </Wrapper>
  );
}
